import * as stylex from '@stylexjs/stylex';
import { useEffect, useRef, useState } from 'react';
import { MdCampaign, MdPlace, MdChatBubble, MdVerifiedUser, MdShield, MdVisibility } from 'react-icons/md';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { UserAvatar } from '~components/UserAvatar';
import { SiteHeader } from '~components/SiteHeader';
import { LogoTile } from '~components/Logo';
import { openComingSoon } from '~components/ComingSoon';
import { legalPaths } from '~pages/Legal/entity';
import { Diorama } from './components/Diorama';
import { PhotoWall } from './components/PhotoWall';
import { styles } from './styles';
import shotMap from '../../assets/shot-map.webp';
import shotChat from '../../assets/shot-chat.webp';

// The app's splash screen quotes, led by the one that IS the thesis.
const QUOTES: {
  text: string;
  author?: string;
}[] = [{
  text: '“Happiness is only real when shared.”',
  author: 'Alexander Supertramp'
}, {
  text: '“Actually connecting people”',
  author: 'Me'
}, {
  text: '“Life is either a daring adventure or nothing at all.”',
  author: 'Helen Keller'
}, {
  text: '“Life is meant for good friends and great adventures.”'
}, {
  text: '“The best things in life are the people we love, the places we\'ve been, and the memories we\'ve made along the way.”'
}, {
  text: '“We are all travelers in the wilderness of this world, and the best we can find in our travels is an honest friend.”',
  author: 'Robert Louis Stevenson'
}, {
  text: '“A journey is best measured in friends, rather than miles.”',
  author: 'Tim Cahill'
}, {
  text: '“Life begins at the end of your comfort zone.”',
  author: 'Neale Donald Walsch'
}, {
  text: '“Do one thing every day that scares you.”',
  author: 'Eleanor Roosevelt'
}, {
  text: '“Man cannot discover new oceans unless he has the courage to lose sight of the shore.”',
  author: 'André Gide'
}];
function RotatingQuote() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  // Every 10s: fade the quote out (800ms), swap it, fade back in.
  useEffect(() => {
    let timeout: number;
    const interval = window.setInterval(() => {
      setVisible(false);
      timeout = window.setTimeout(() => {
        setIndex(i => (i + 1) % QUOTES.length);
        setVisible(true);
      }, 800);
    }, 10000);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, []);
  const quote = QUOTES[index];
  return <figure {...stylex.props(styles.quote, styles.quoteSwap, !visible && styles.quoteHidden)}>
      <blockquote {...stylex.props(styles.quoteText)}>{quote.text}</blockquote>
      {quote.author && <figcaption {...stylex.props(styles.quoteAuthor)}>— {quote.author}</figcaption>}
    </figure>;
}

// A dotted walking-route segment (like the app's map) linking sections —
// the page reads as a journey from question to meetup. `end` drops the
// destination pin.
function RouteDivider({
  flip = false,
  end = false
}: {
  flip?: boolean;
  end?: boolean;
}) {
  return <div {...stylex.props(styles.route)} aria-hidden="true">
      <svg viewBox="0 0 100 110" {...stylex.props(styles.routeSvg)}>
        <path d={flip ? 'M 66 0 C 30 36 72 74 44 110' : 'M 34 0 C 70 36 28 74 56 110'} fill="none" stroke="#90A4AE" strokeWidth="3" strokeDasharray="0.5 9" strokeLinecap="round" />
      </svg>
      {end && <MdPlace size={28} {...stylex.props(styles.routePin)} />}
    </div>;
}
export function Home() {
  const compareRef = useRef<HTMLDivElement | null>(null);
  const [compareSeen, setCompareSeen] = useState(false);

  // One deliberate scroll moment: when the comparison scrolls into view, the
  // events-app card sinks away and the Tribys card lifts.
  useEffect(() => {
    const el = compareRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setCompareSeen(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.35
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <>
      <SiteHeader ctaHref="#get" />
      <div {...stylex.props(styles.page)}>
      <main>
        <section {...stylex.props(styles.hero)}>
          <div>
            <p {...stylex.props(styles.eyebrow)}>Get Out There</p>
            <h1 {...stylex.props(styles.h1)}>
              Come find your tribe.
              {' '}
              <span {...stylex.props(styles.h1Accent)}>Your people are closer than you think.</span>
            </h1>
            <p {...stylex.props(styles.heroSub)}>
              Pick a spot you actually want to go to — a ramen bar, a climbing
              gym, a night market — and share a great experience with
              like-minded people. Life was never meant to be shared through a
              screen.
            </p>
            <div {...stylex.props(styles.heroActions)}>
              <a href="#get" {...stylex.props(styles.primaryCta)}>Get the app</a>
              <a href="#how" {...stylex.props(styles.ghostCta)}>See how it works</a>
            </div>
          </div>
          <Diorama />
        </section>

        <RouteDivider />

        <RotatingQuote />

        <section {...stylex.props(styles.section)}>
          <p {...stylex.props(styles.eyebrow)}>The difference</p>
          <h2 {...stylex.props(styles.h2)}>This is not an events app.</h2>
          <div ref={compareRef} {...stylex.props(styles.compare)}>
            <div {...stylex.props(styles.compareCard, styles.compareOld, compareSeen && styles.compareOldSeen)}>
              <span {...stylex.props(styles.compareTag)}>
                <MdCampaign size={16} />
                Every other app
              </span>
              <p {...stylex.props(styles.compareQuote, styles.compareQuoteOld)}>
                “There's an event happening at The Warehouse. Everyone who
                wants to come is invited.”
              </p>
              <p {...stylex.props(styles.compareNote)}>
                A crowd, a stage, a room full of strangers.
              </p>
            </div>
            <div {...stylex.props(styles.compareCard, styles.compareNew, compareSeen && styles.compareNewSeen)}>
              <span {...stylex.props(styles.compareTag, styles.compareTagNew)}>
                <MdChatBubble size={16} />
                Tribys
              </span>
              <p {...stylex.props(styles.compareQuote)}>
                “There's this spot I've been wanting to try — anyone want to
                come with me?”
              </p>
              <div {...stylex.props(styles.compareReplies)}>
                <UserAvatar name="Dev Mehta" size={28} />
                <UserAvatar name="Lena Bauer" size={28} />
                <UserAvatar name="Sam Okafor" size={28} />
                <span {...stylex.props(styles.replyText)}>are in.</span>
              </div>
            </div>
          </div>

          <p {...stylex.props(styles.differenceStatement)}>
            This isn't about organizing events. It's about finding the right people to share and experience life. 
          </p>

        </section>

        <RouteDivider flip />

        <section id="how" {...stylex.props(styles.section)}>
          <p {...stylex.props(styles.eyebrow)}>How it works</p>
          <h2 {...stylex.props(styles.h2)}>Find. Chat. Live.</h2>

          <div {...stylex.props(styles.leg)}>
            <div>
              <span {...stylex.props(styles.stepNum)}>1</span>
              <h3 {...stylex.props(styles.legTitle)}>Find or create a triby</h3>
              <p {...stylex.props(styles.legText)}>
                Browse the map for tribys forming near you — or pick a spot
                you've been meaning to try and start your own.
              </p>
            </div>
            <img {...stylex.props(styles.legShot, styles.legShotA)} src={shotMap} alt="The app's map: tribys forming at spots near you" loading="lazy" />
          </div>

          <RouteDivider flip />

          <div {...stylex.props(styles.leg, styles.legReverse)}>
            <div {...stylex.props(styles.legCopyReverse)}>
              <span {...stylex.props(styles.stepNum)}>2</span>
              <h3 {...stylex.props(styles.legTitle)}>Break the ice</h3>
              <p {...stylex.props(styles.legText)}>
                Get to know each other in the triby's chat before you ever
                meet. Jokes, plans, GIFs — by the day of, they're not
                strangers.
              </p>
            </div>
            <img {...stylex.props(styles.legShot, styles.legShotB, styles.legShotReverse)} src={shotChat} alt="A triby chat: breaking the ice before meeting up" loading="lazy" />
          </div>

          <RouteDivider />

          <div {...stylex.props(styles.destination)}>
            <div {...stylex.props(styles.destinationInner)}>
              <span {...stylex.props(styles.destinationChip)}>
                <MdPlace size={16} />
                Step 3 — the destination
              </span>
              <h3 {...stylex.props(styles.destinationTitle)}>Meet in real life.</h3>
              <p {...stylex.props(styles.destinationSub)}>
                Phones away. This is what it was all for — real tribys, out in
                the world.
              </p>
            </div>
            <PhotoWall />
            <p {...stylex.props(styles.dedication)}>
              A dedication to all the amazing people I have met.
            </p>
          </div>
        </section>

        <RouteDivider />

        <section {...stylex.props(styles.section, styles.manifesto)}>
          <h2 {...stylex.props(styles.h2, styles.manifestoTitle)}>Never walk in alone.</h2>
          <p {...stylex.props(styles.manifestoText)}>
            Crowds are hard. You don't know anyone, starting a conversation
            feels like work, and just showing up by yourself takes courage. A
            triby flips that: find your people before you go. Chat, joke,
            break the ice — so when you walk in, you're not approaching
            strangers. Your friends are already waiting for you.
          </p>
        </section>

        <RouteDivider flip />

        <section {...stylex.props(styles.section)}>
          <p {...stylex.props(styles.eyebrow)}>Safety</p>
          <h2 {...stylex.props(styles.h2)}>Safe is the whole point.</h2>
          <p {...stylex.props(styles.safetyLead)}>
            Meeting strangers in real life only works if you can trust who's
            on the other side. Nothing matters more to us than a safe
            community — and we will do everything in our power to keep it
            that way.
          </p>
          <div {...stylex.props(styles.safetyGrid)}>
            <div {...stylex.props(styles.safetyCard)}>
              <MdVerifiedUser size={24} {...stylex.props(styles.safetyIconBlue)} />
              <h3 {...stylex.props(styles.h3)}>Verified identities</h3>
              <p {...stylex.props(styles.stepText)}>
                Members can verify who they are with a government-ID check.
                Verified profiles carry the blue badge everywhere in the app.
              </p>
            </div>
            <div {...stylex.props(styles.safetyCard)}>
              <MdShield size={24} {...stylex.props(styles.safetyIconCoral)} />
              <h3 {...stylex.props(styles.h3)}>Verified-only tribys</h3>
              <p {...stylex.props(styles.stepText)}>
                Creating a triby? Flip one switch and only verified members
                can join. You decide who gets a seat at your table.
              </p>
            </div>
            <div {...stylex.props(styles.safetyCard)}>
              <MdVisibility size={24} {...stylex.props(styles.safetyIconCyan)} />
              <h3 {...stylex.props(styles.h3)}>Watched around the clock</h3>
              <p {...stylex.props(styles.stepText)}>
                Photos and messages are screened automatically, and reporting
                someone takes two taps. Bad actors don't stay long.
              </p>
            </div>
          </div>
        </section>

        <RouteDivider end />

        <section id="get" {...stylex.props(styles.section)}>
          <div {...stylex.props(styles.ctaPanel)}>
            <h2 {...stylex.props(styles.h2, styles.ctaTitle)}>
              Your people are closer than you think.
            </h2>
            <div {...stylex.props(styles.storeRow)}>
              <button type="button" onClick={openComingSoon} {...stylex.props(styles.storeButton)}>
                <FaApple size={20} />
                App Store
              </button>
              <button type="button" onClick={openComingSoon} {...stylex.props(styles.storeButton)}>
                <FaGooglePlay size={18} />
                Google Play
              </button>
            </div>
            <p {...stylex.props(styles.ctaNote)}>Free on iOS and Android.</p>
          </div>
        </section>
      </main>

      <footer {...stylex.props(styles.footer)}>
        <LogoTile size={32} />
        <span {...stylex.props(styles.footerText)}>
          © {new Date().getFullYear()} Tribys LLC — Get Out There, Come find your tribe.
        </span>
        <nav {...stylex.props(styles.footerLinks)}>
          <a href={legalPaths.privacy} {...stylex.props(styles.footerLink)}>
            Privacy Policy
          </a>
          <a href={legalPaths.biometric} {...stylex.props(styles.footerLink)}>
            Biometric &amp; Consumer Health Data
          </a>
          <a href={legalPaths.terms} {...stylex.props(styles.footerLink)}>
            Terms of Service
          </a>
          <a href={legalPaths.guidelines} {...stylex.props(styles.footerLink)}>
            Community Guidelines
          </a>
        </nav>
      </footer>
      </div>
    </>;
}